import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import {
  useGetDeployedEntitySnapshot,
  useSearchDeployedInterfacePageEmbeddedEntities,
} from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import _toNumber from 'lodash/toNumber';
import { useMemo } from 'react';
import { useDeployedInterfaceDetails } from '../../../../context/PublishedInterfaceProvider';
import {
  DEPLOYED_ALLOWED_ENTITY_TYPES,
  EMBEDDED_ENTITIES_EXCLUDE_FIELDS,
} from '../../../../utils/interfaceQueryFilters';
import {
  getAllPagesFromEmbedded,
  normalizeEmbeddedDependents,
} from '../../../../utils/normalizeEmbeddedDependents';
import type { UseFetchResourcesHookType } from '../../types';
import useFetchCustomBlocksForPages from '../../useFetchCustomBlocks/useFetchCustomBlocksForPages';
import { useFetchDeployedInterfacePageNamespacesForPages } from '../data/useFetchDeployedInterfaceNamespaces/useFetchDeployedInterfacePageNamespacesForPages';

const useFetchDeployedInterfaceResourcesByVersion: UseFetchResourcesHookType = ({
  interfaceId,
  interfaceComponent,
  mode,
}) => {
  const { deployedVersion } = useDeployedInterfaceDetails();

  const {
    data: interfaceRecord,
    isLoading: isLoadingInterfaceRecord,
    error: errorInterfaceRecord,
  } = useGetDeployedEntitySnapshot<InterfaceEntity | undefined>(
    UAEntityTypes.Interface,
    interfaceId,
    {
      version: deployedVersion,
    },
  );

  // Deep traversal: returns the page entity, all nested pages, and every data source for them.
  // Once orval regenerates with searchDeployedInterfacePageEmbeddedEntities marked as useQuery in the config,
  // this hook variant exposes a query interface (not a mutation) keyed by the request body.
  const versionNumber = _toNumber(deployedVersion);
  const {
    data: embeddedResp,
    isLoading: isLoadingPage,
    error: errorPage,
  } = useSearchDeployedInterfacePageEmbeddedEntities(
    {
      entityId: interfaceComponent.id ?? '',
      version: versionNumber,
      excludeFields: EMBEDDED_ENTITIES_EXCLUDE_FIELDS,
      allowedEntityTypes: DEPLOYED_ALLOWED_ENTITY_TYPES,
    },
    {
      query: {
        enabled: Boolean(interfaceComponent.id) && Boolean(versionNumber),
        retry: 3,
        staleTime: Infinity,
      },
    },
  );

  const { interfacePage, dataSources, embeddedPages, embeddedInterfaceRecords } = useMemo(
    () => normalizeEmbeddedDependents(embeddedResp, true),
    [embeddedResp],
  );

  const allPages = useMemo(
    () => getAllPagesFromEmbedded(interfacePage, embeddedPages),
    [interfacePage, embeddedPages],
  );

  const {
    namespaces,
    isLoading: isLoadingNamespaces,
    error: errorNamespaces,
  } = useFetchDeployedInterfacePageNamespacesForPages({
    interfaceId,
    pages: allPages,
    interfaceRecord,
    mode,
  });

  const interfacePageNamespace = interfacePage?.id ? namespaces?.[interfacePage.id] : undefined;

  const {
    customBlocks,
    customBlocksSnapshots,
    isLoading: isLoadingCustomBlocks,
  } = useFetchCustomBlocksForPages(allPages);

  const _isLoading =
    isLoadingInterfaceRecord || isLoadingPage || isLoadingNamespaces || isLoadingCustomBlocks;
  const _error = errorInterfaceRecord || errorPage || errorNamespaces;

  return {
    isLoading: _isLoading,
    error: _error,
    data: interfaceRecord,
    dataSources,
    interfacePage,
    interfacePageNamespace,
    namespaces,
    embeddedPages,
    embeddedInterfaceRecords,
    customBlocks,
    customBlocksSnapshots,
  };
};

export default useFetchDeployedInterfaceResourcesByVersion;
